import type * as React from "react";
import { Eyebrow } from "@/components/shared/Eyebrow";
import { cn } from "@/lib/utils";

type PageHeaderProps = {
  /**
   * Optional section label above the title, in the nymbl.app eyebrow style
   * (DM Mono, uppercase, violet) — e.g. the nav group the screen sits under.
   */
  eyebrow?: React.ReactNode;
  /** The screen's name. Rendered as the page's only `h1`. */
  title: React.ReactNode;
  /**
   * One short sentence saying what the screen is for. Optional — omit it rather
   * than padding it out. Capped at `max-w-2xl` so it stays a scannable line
   * instead of stretching the full width of a wide table.
   */
  description?: React.ReactNode;
  /** Primary (and at most one secondary) action for the screen. */
  actions?: React.ReactNode;
  className?: string;
};

/**
 * The one page header.
 *
 * Fixes a real drift problem: page titles were rendering at six different sizes
 * (18 / 20 / 22 / 24 / 26 / 30px) with four different row alignments, so the
 * title moved as you navigated. Title size, weight, description size and the
 * action slot are all fixed here — callers pass content, never classes for
 * those parts.
 *
 * Title is `text-2xl` (`--fs-2xl`, 24px) Raleway in heading ink; description
 * is `text-sm` (`--fs-sm`, 14px) muted.
 */
export function PageHeader({ eyebrow, title, description, actions, className }: PageHeaderProps) {
  return (
    <header
      data-slot="page-header"
      className={cn("flex flex-wrap items-start justify-between gap-4", className)}
    >
      <div className="flex min-w-0 flex-col gap-[var(--page-header-gap)]">
        {eyebrow ? <Eyebrow className="mb-1">{eyebrow}</Eyebrow> : null}
        <h1 className="font-display text-2xl font-semibold text-heading">{title}</h1>
        {description ? (
          <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}
